/**
 * Units Conversion Modes available.
 * NB: the first key is normally used as the default setting if none found/set.
 */
export enum UnitsConversionModes {
    DEFAULT = 'Default (no conversion)',
    IMPERIAL = 'Imperial',
    METRIC = 'Metric',
}

export const UnitsConversionPreferenceLocalStorageKey: string = 'unitsConversionPreference';

export function getModeFromString(mode: string): UnitsConversionModes {
    /*let keys = Object.keys(UnitsConversionModes).map(x => x.toLowerCase());
    if (keys.indexOf(mode.toLowerCase()) >= 0) {

    }*/

    if (mode && mode.toUpperCase() in UnitsConversionModes) {
        return UnitsConversionModes[mode.toUpperCase()];
    } else {
        const tsOffset = -new Date().getTimezoneOffset();
        console.log(`Detecting default unit conversions using time zone offset: ${tsOffset} minutes`);

        return tsOffset < 0 ? UnitsConversionModes.IMPERIAL : UnitsConversionModes.DEFAULT;
    }
}


export interface IUnitsConverter {
    getConversionSet(sourceUnits: string, parameterGroupOrParamId?: string): IUnitsConversionSet | null;
    getConvertedUnits(sourceUnits: string, parameterGroupOrParamId?: string): string;
    convert(value: number, sourceUnits: string, parameterGroupOrParamId?: string): number;
}

export interface IUnitsConversionSet {
    sourceUnits: string;
    convertedUnits: string;
    //factor: number;
    convertValue: (value: number | any) => number | any; //: NB: not calling this 'convert' to avoid confusion with IUnitsConverter.convert()... Also allowing for non numerical value to be provided (unexpected, but just in case to avoid page failures), in which case the value is returned unmodified.
}

export class UnitsConversionSets { // : { [key: string]: IUnitsConversionSet } =
    public static find(sourceUnits: string, mode: UnitsConversionModes, isVisibility: boolean = false): IUnitsConversionSet | null {
        if (mode == UnitsConversionModes.IMPERIAL) {
            if (sourceUnits == 'm') {
                return isVisibility ? this.meters_to_miles : this.meters_to_feet;
            }

            if (sourceUnits == 'm/s' || sourceUnits == 'mps') {
                return this.mps_to_kts;
            }

            if (sourceUnits == 'm/s²' || sourceUnits == 'mps²') {
                return this.mps2_to_ftps2;
            }

            if (sourceUnits == 'degC' || sourceUnits == '°C') {
                return this.degC_to_degF;
            }
        }

        if (mode == UnitsConversionModes.METRIC) {
            if (sourceUnits == 'ft') {
                return this.feet_to_meters;
            }

            if (sourceUnits == 'mi') {
                return this.miles_to_meters;
            }

            if (sourceUnits == 'kts') {
                return this.kts_to_mps;
            }

            if (sourceUnits == 'ft/s²') {
                return this.ftps2_to_mps2;
            }

            if (sourceUnits == 'degF' || sourceUnits == '°F') {
                return this.degF_to_degC;
            }
        }

        return null;
    }

    //: metric to imperial:
    public static meters_to_feet: IUnitsConversionSet = {
        sourceUnits: 'm',
        convertedUnits: 'ft',
        convertValue: (value: number | any) => {
            return typeof value == 'number' ? value * 3.28084 : value;
        },
    };
    public static meters_to_miles: IUnitsConversionSet = {
        sourceUnits: 'm',
        convertedUnits: 'mi',
        convertValue: (value: number | any) => {
            return typeof value == 'number' ? value * 0.000621371 : value;
        },
    };
    public static mps_to_kts = {
        sourceUnits: 'm/s',
        convertedUnits: 'kts',
        convertValue: (value: number | any) => {
            return typeof value == 'number' ? value * 1.94384 : value;
        },
    };
    public static mps2_to_ftps2: IUnitsConversionSet = { //: m/s² to foot per square second (SEE: https://www.unitjuggler.com/convert-acceleration-from-ms2-to-fts2.html)
        sourceUnits: 'm/s²',
        convertedUnits: 'ft/s²',
        convertValue: (value: number | any) => {
            return typeof value == 'number' ? value * 3.28084 : value;
        },
    };
    public static degC_to_degF = {
        sourceUnits: 'degC',
        convertedUnits: '°F',
        convertValue: (value: number | any) => {
            return typeof value == 'number' ? (value * 1.8) + 32 : value;
        },
    };


    //: imperial to metric:
    public static kts_to_mps: IUnitsConversionSet = {
        sourceUnits: 'kts',
        convertedUnits: 'm/s',
        convertValue: (value: number | any) => {
            return typeof value == 'number' ? value * 0.514444 : value;
        },
    };
    public static feet_to_meters: IUnitsConversionSet = {
        sourceUnits: 'ft',
        convertedUnits: 'm',
        convertValue: (value: number | any) => {
            return typeof value == 'number' ? value * 0.3048 : value;
        },
    };
    public static miles_to_meters: IUnitsConversionSet = {
        sourceUnits: 'mi',
        convertedUnits: 'm',
        convertValue: (value: number | any) => {
            return typeof value == 'number' ? value * 1609.34 : value;
        },
    };
    public static ftps2_to_mps2: IUnitsConversionSet = { //: (SEE: https://www.unitjuggler.com/convert-acceleration-from-ms2-to-fts2.html)
        sourceUnits: 'ft/s²',
        convertedUnits: 'm/s²',
        convertValue: (value: number | any) => {
            return typeof value == 'number' ? value * 0.3048 : value;
        },
    };
    public static degF_to_degC = {
        sourceUnits: 'degF',
        convertedUnits: '°C',
        convertValue: (value: number | any) => {
            return typeof value == 'number' ? (value - 32) / 1.8 : value;
        },
    };
}